import React, { ChangeEvent, useState } from "react";
import { ContactUsStaticData, Images } from "../../utils/constant";
import Input, { TextInput } from "../common/Input";
import Heading from "../common/heading";
import SubHeading from "../common/subheading";
import toast from "react-hot-toast";
import useRecaptchaV3 from "../../utils/useRecaptchaV3";
import "./style.css";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const ContactUs = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const cardItems = useRef<
    HTMLAnchorElement[] | HTMLHeadElement[] | HTMLParagraphElement[]
  >([]);
  const siteKey = process.env.REACT_APP_RECAPTCHA_KEY || "";
  const { executeRecaptcha, isRecaptchaReady } = useRecaptchaV3(siteKey);
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [error, setError] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [BtnDisabled, setBtnDisabled] = useState(false);

  const validateField = (name: string, value: string) => {
    let errorMsg = "";

    switch (name) {
      case "name":
        if (!value.trim()) errorMsg = "Name is required";
        else if (!/^[a-zA-Z\s]+$/.test(value))
          errorMsg = "Name should only contain letters and spaces";
        break;

      case "email":
        if (!value.trim()) errorMsg = "Email is required";
        else if (
          !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
        )
          errorMsg = "Enter a valid email address";
        break;

      case "message":
        if (!value.trim()) errorMsg = "Message is required";
        else if (value.trim().length < 10)
          errorMsg = "Message should be at least 10 characters";
        break;

      default:
        break;
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    return errorMsg === "";
  };

  const formHandler = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Live validation
    validateField(name, value);
  };

  const formSubmitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isRecaptchaReady) {
      toast.error("reCAPTCHA is not ready. Please try again later.");
      return;
    }
    const isNameValid = validateField("name", form.name);
    const isEmailValid = validateField("email", form.email);
    const isMessageValid = validateField("message", form.message);

    if (!isNameValid || !isEmailValid || !isMessageValid) return;

    console.log("Form submitted ✅", form);

    const toastId = toast.loading("Sending message...");
    try {
      const token = await executeRecaptcha("submit_form");
      const { name, email, message: description } = form;
      const dataToSend = { name, email, description, token };
      const response = await fetch(baseUrl + "/api/v1/common/contact-us", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });
      const res = await response.json();
      if (res.code === 200) {
        setBtnDisabled(true);
        toast.success(res.message || "Message sent successfully!", {
          id: toastId,
        });
      } else {
        toast.error(res.message || "Something went wrong!", {
          id: toastId,
        });
      }
      setForm({ name: "", email: "", message: "" });
      setError({ name: "", email: "", message: "" });
    } catch (error: any) {
      console.log("Error :", error);
      toast.error(error.message || "Something went wrong!", { id: toastId });
    }
  };

  const tl = useRef<gsap.core.Timeline | null>(null);

  useGSAP(
    () => {
      tl.current = gsap.timeline({
        scrollTrigger: {
          trigger: cardRef.current,
          start: "top 85%", // Softer trigger, enter gently
          end: "bottom 60%",
          // scrub: 0.5, // Smooth progress
          // markers: true, // Remove for production
          once: true, // Animate only once
        },
      });

      tl.current.from(cardRef.current, {
        x: -80, // Subtle slide, not overdone
        autoAlpha: 0,
        duration: 1.1, // Slightly longer for grace
        ease: "power2.out", // Softer ease for polish
      });

      tl.current.from(
        cardItems.current,
        {
          y: 40, // Less aggressive entrance
          autoAlpha: 0,
          scale: 0.95, // Slight upscaling for lively feel
          stagger: {
            amount: 0.6, // Spread cards smoothly across timeline
            from: "start", // Sequential reveal from top
            ease: "ease.in", // Easy-in stagger if desired
          },
          duration: 0.8,
          ease: "power2.out", // Consistency in feel
        },
        "-=0.8" // Overlap card entrance with container
      );
    },
    { scope: containerRef }
  );

  return (
    <section
      style={{ backgroundImage: `url(${Images.contactUsBg})` }}
      className="common-section-container contact-us-section-container"
      id="contact-us"
      ref={containerRef}
    >
      <header className="common-heading-container">
        <Heading heading={ContactUsStaticData.heading} />
        <SubHeading subheading={ContactUsStaticData.subheading} />
      </header>
      <form className="contact-form-container" onSubmit={formSubmitHandler}>
        <div className="position-relative">
          <Input
            value={form.name}
            onChange={formHandler}
            label={"Name"}
            name="name"
            pattern="[a-zA-Z\s]+"
            type="text"
          />
          {error.name && <p className="error-text">{error.name}</p>}
        </div>
        <div className="position-relative">
          <Input
            value={form.email}
            onChange={formHandler}
            label={"Email"}
            name="email"
            pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
            type="email"
          />
          {error.email && <p className="error-text">{error.email}</p>}
        </div>
        <div className="position-relative">
          <TextInput
            value={form.message}
            label="Message"
            name="message"
            onChange={formHandler}
            attachFile={false}
          />
          {error.message && <p className="error-text">{error.message}</p>}
        </div>
        <button
          className="contact-form-button"
          type="submit"
          disabled={BtnDisabled}
          style={{
            cursor: BtnDisabled ? "not-allowed" : "pointer",
          }}
        >
          Send
          <img
            src={Images.sendIcon}
            alt="send icon"
            className="contact-form-button-icon"
          />
        </button>
      </form>

      <div
        className="get-in-touch-container"
        style={{
          backgroundImage: `url(${Images.getInTouch})`,
        }}
        ref={cardRef}
      >
        <h4
          className="get-in-touch-heading"
          ref={(el) => {
            if (el) {
              cardItems.current[0] = el;
            }
          }}
        >
          {ContactUsStaticData.getInTouch.heading}
        </h4>
        <p
          className="get-in-touch-subheading"
          ref={(el) => {
            if (el) {
              cardItems.current[1] = el;
            }
          }}
        >
          {ContactUsStaticData.getInTouch.subheading}
        </p>

        <div className="get-in-touch-cards-container">
          {ContactUsStaticData.getInTouch.cards.map((card, index) => {
            return (
              <a
                key={index}
                href={card.link}
                target="_blank"
                rel="noopener noreferrer"
                className="get-in-touch-card"
                ref={(el) => {
                  if (el) cardItems.current[index + 2] = el;
                }}
              >
                <img
                  src={card.icon}
                  alt={card.text}
                  className="get-in-touch-card-icon"
                />
                <p className="get-in-touch-card-text">{card.text}</p>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
